// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback } from 'react'
import { formatUsDateLocal } from '../../api/format'
import {
  Search, Loader2, AlertCircle, X, Power, UserCheck, ShieldCheck,
  ChevronRight, Building2, Key, LogOut, Lock, Unlock, Smartphone,
  Trash2, Copy, History, Monitor,
} from 'lucide-react'
import * as api from '../../api/client'
import ManufacturerAccessSection from '../../components/admin/ManufacturerAccessSection'
import FacilityAccessSection from '../../components/admin/FacilityAccessSection'
import SiteOfCareScopeSection from '../../components/admin/SiteOfCareScopeSection'
import UserGroupsSection from '../../components/admin/UserGroupsSection'
import ProviderDrugFilterSection from '../../components/admin/ProviderDrugFilterSection'
import { useAuth } from '../../auth/AuthContext'
import { useModules } from '../../auth/ModulesContext'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import { friendlyError } from '../../api/errors'

/**
 * Users tab — lists all accounts, lets admin click into a row for
 * detail (roles + permissions) and perform actions:
 *   - Deactivate / reactivate
 *   - Assign role / revoke role
 *
 * Uses the WI-006 endpoints under /api/users, /api/roles.
 */
export default function UsersTab() {
  const [items, setItems] = useState<UserListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams()
      if (search.trim())  params.set('q', search.trim())
      if (statusFilter)   params.set('status', statusFilter)
      params.set('limit', '200')
      const r = await api.apiFetch(`/users?${params}`, { credentials: 'include' })
      if (!r.ok) throw await api.parseApiError(r)
      const data = await r.json()
      setItems(data.items)
    } catch (e: any) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter])

  useEffect(() => { reload() }, [reload])

  // Reset to page 1 when the filter inputs change (a length resetKey misses two
  // different result sets that happen to have the same row count).
  // The header funnels filter the whole loaded set; paging happens after.
  const fx = useTableFunnels(items, USER_FUNNELS)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${statusFilter}|${search}|${fx.sig}`)

  return (
    <>
      <div style={toolbar}>
        <div style={searchBox}>
          <Search size={14} style={searchIcon} aria-hidden="true" />
          <input
            type="text"
            placeholder="Search email or display name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={searchInput}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={filterSelect}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="locked">Locked</option>
          <option value="disabled">Disabled</option>
          <option value="pending">Pending</option>
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-text-mute)' }}>
          {items.length} user{items.length === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? (
        <div style={loadingWrap}>
          <Loader2 size={20} className="spin" />
          <span style={{ marginLeft: 10, color: 'var(--color-text-soft)' }}>Loading users…</span>
        </div>
      ) : error ? (
        <div style={errorWrap}>
          <AlertCircle size={18} aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : items.length === 0 ? (
        <div style={emptyWrap}>
          <p style={{ fontSize: 14, color: 'var(--color-text-soft)' }}>
            No users match those filters.
          </p>
        </div>
      ) : (
        <>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="users" />
        {fx.chips}
        <div style={cardStyle}>
          <div style={tableHeader}>
            <div style={{ flex: 1.5 }}>Email {fx.funnel('email')}</div>
            <div style={{ flex: 1.1 }}>Display name {fx.funnel('name')}</div>
            <div style={{ width: 80 }}>Status {fx.funnel('status')}</div>
            <div style={{ flex: 1.3 }}>Access {fx.funnel('access')}</div>
            <div style={{ flex: 1 }}>Roles {fx.funnel('roles')}</div>
            <div style={{ width: 110 }}>Last login {fx.funnel('login')}</div>
            <div style={{ width: 24 }} aria-hidden="true" />
          </div>
          {pageRows.map(u => (
            <button
              key={u.id}
              onClick={() => setSelectedId(u.id)}
              style={{
                ...tableRow,
                ...(selectedId === u.id ? tableRowActive : {}),
              }}
            >
              <div style={{ flex: 1.5, minWidth: 0, ...truncate }}>
                <span className="email-text" style={{ fontSize: 13 }}>{u.email}</span>
                {u.is_synthetic && <span style={syntheticPill}>synthetic</span>}
              </div>
              <div style={{ flex: 1.2, fontSize: 13, ...truncate }}>{u.display_name}</div>
              <div style={{ width: 80 }}>
                <StatusPill status={u.status} />
              </div>
              <div style={{ flex: 1.3, minWidth: 0, ...truncate }}>
                {u.pharmacy_id
                  ? <span style={{ fontSize: 12.5 }}>{u.pharmacy_name || 'Pharmacy'}</span>
                  : <span style={saasBadge}>SaaS · Owl Consultations</span>}
              </div>
              <div style={{ flex: 1, fontSize: 12.5, color: 'var(--color-text-soft)', ...truncate }}>
                {roleLabel(u.roles) || <span style={{ color: 'var(--color-text-mute)' }}>none</span>}
              </div>
              <div style={{ width: 110, fontSize: 12, color: 'var(--color-text-mute)' }}>
                {u.last_login_at ? relTime(u.last_login_at) : 'never'}
              </div>
              <ChevronRight size={14} style={{ color: 'var(--color-text-mute)', flexShrink: 0 }} />
            </button>
          ))}
        </div>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="users" />
        </>
      )}

      {selectedId && (
        <UserDrawer
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={reload}
        />
      )}
    </>
  )
}

// ============================================================================
// Drawer
// ============================================================================

interface UserDetail {
  account: {
    id: string; email: string; display_name: string; status: string;
    email_verified: boolean; is_synthetic: boolean;
    last_login_at: string | null; last_login_ip: string | null;
    failed_login_count: number; locked_until: string | null;
    created_at: string; deactivated_at: string | null;
    pharmacy_id: string | null;
    mfa_required?: boolean; mfa_enabled?: boolean;
    /** "sso" = EntraID/federated employee (no app-managed password/MFA); "password" = local account. */
    auth_source?: 'sso' | 'password';
    external_idp?: string | null;
  }
  roles: { id: string; name: string; description: string | null; is_system: boolean; granted_at: string }[]
  permissions: string[]
}

function UserDrawer({ userId, onClose, onChanged }: {
  userId: string; onClose: () => void; onChanged: () => void
}) {
  const { me, hasPermission } = useAuth()
  // Cross-pharmacy reassignment is a SaaS/platform-only operation (backend rejects bound actors
  // with 404). A bound pharmacy admin must not even see the picker for their own pharmacy view —
  // but a platform admin (platform.access) keeps it even while they hold a pharmacy assignment,
  // matching the backend's RestrictToPharmacy rule (RBAC ticket 2026-08-12: the raw pharmacy_id
  // check hid the picker from assigned SaaS admins, so they couldn't even unassign themselves).
  const canReassignPharmacy = !me?.pharmacy_id || hasPermission('platform.access')
  const canWriteUsers = hasPermission('admin.users.deactivate')
  const [detail, setDetail] = useState<UserDetail | null>(null)
  const [allRoles, setAllRoles] = useState<RoleSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [revealedReset, setRevealedReset] = useState<{ link: string; email: string } | null>(null)

  const reload = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [d, rs] = await Promise.all([
        api.apiFetch(`/users/${userId}`, { credentials: 'include' }).then(throwIfBad),
        api.apiFetch(`/roles`, { credentials: 'include' }).then(throwIfBad),
      ])
      setDetail(d)
      setAllRoles(rs.items)
    } catch (e: any) {
      setError(friendlyError(e))
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => { reload() }, [reload])

  // Escape closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function callAction(path: string, body?: any) {
    setBusy(true); setError(null)
    try {
      const r = await api.apiFetch(`${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      })
      if (!r.ok) throw await api.parseApiError(r)
      await reload()
      onChanged()
    } catch (e: any) {
      setError(friendlyError(e))
    } finally {
      setBusy(false)
    }
  }

  const userRoleIds = new Set(detail?.roles.map(r => r.id) ?? [])
  const availableRoles = allRoles.filter(r => !userRoleIds.has(r.id))

  return (
    <>
      <div style={backdropStyle} onClick={onClose} aria-hidden="true" />
      <aside style={drawerStyle} role="dialog" aria-label="User detail">
        <header style={drawerHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>User detail</h3>
          <button onClick={onClose} style={iconBtn} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        {loading ? (
          <div style={loadingWrap}><Loader2 size={20} className="spin" /></div>
        ) : error ? (
          <div style={{ ...errorWrap, margin: 16 }}>
            <AlertCircle size={18} /> <span>{error}</span>
          </div>
        ) : detail && (
          <div style={drawerBody}>
            {/* Identity */}
            <section>
              <h4 style={drawerSectionTitle}>Identity</h4>
              <dl style={kvList}>
                <KV label="Display name">{detail.account.display_name}</KV>
                <KV label="Email">
                  {detail.account.email}
                  {detail.account.email_verified
                    ? <span style={miniBadge}>verified</span>
                    : <span style={{ ...miniBadge, background: '#fef3c7', color: '#854d0e' }}>unverified</span>}
                </KV>
                <KV label="Status"><StatusPill status={detail.account.status} /></KV>
                <KV label="Synthetic">{detail.account.is_synthetic ? 'Yes' : 'No'}</KV>
                <KV label="Last login">
                  {detail.account.last_login_at
                    ? `${relTime(detail.account.last_login_at)} from ${detail.account.last_login_ip ?? 'unknown'}`
                    : 'never'}
                </KV>
                <KV label="Failed logins">{detail.account.failed_login_count}</KV>
                <KV label="Created">{relTime(detail.account.created_at)}</KV>
              </dl>
            </section>

            {/* MFA — Owner/Admin only (security.mfa.manage). External/password users. */}
            {hasPermission('security.mfa.manage') && (
            <section>
              <h4 style={drawerSectionTitle}>Multi-factor authentication</h4>
              <dl style={kvList}>
                <KV label="Required">
                  {detail.account.mfa_required
                    ? <span style={miniBadge}>required</span>
                    : <span style={{ ...miniBadge, background: '#eef2f1', color: '#475569' }}>not required</span>}
                </KV>
                <KV label="Enrolled">{detail.account.mfa_enabled ? 'Yes' : 'No'}</KV>
              </dl>
              <button
                disabled={busy}
                onClick={() => callAction(`/users/${userId}/mfa-required`, { required: !detail.account.mfa_required })}
                style={{ marginTop: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                {detail.account.mfa_required ? 'Remove MFA requirement' : 'Require MFA at login'}
              </button>
              <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '6px 0 0' }}>
                When required, the user must set up an authenticator app at next sign-in and enter a code every login.
                Removing the requirement keeps any existing enrollment.
              </p>
            </section>
            )}

            {/* Account actions — primary controls a SaaS owner reaches for */}
            <section>
              <h4 style={drawerSectionTitle}>Account actions</h4>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {/* Reset password — local-credential accounts only. EntraID/SSO employees have no
                    app-managed password (EntraID owns it), so this action doesn't apply to them. */}
                {detail.account.status === 'active' && detail.account.auth_source !== 'sso' && (
                  <button
                    onClick={async () => {
                      try {
                        const r = await api.adminTriggerPasswordReset(userId)
                        setRevealedReset({ link: r.reset_link, email: r.email })
                      } catch (e) { setError(friendlyError(e)) }
                    }}
                    disabled={busy}
                    style={secondaryBtn}
                  >
                    <Key size={14} aria-hidden="true" /> Reset password
                  </button>
                )}

                {/* Reset MFA — local-credential accounts only. SSO employees' MFA is enforced in
                    EntraID, not account_mfa, so resetting here would be a no-op and misleading. */}
                {detail.account.status === 'active' && detail.account.auth_source !== 'sso' && (
                  <button
                    onClick={() => {
                      if (confirm(`Reset MFA enrollment for ${detail.account.email}? They'll be prompted to re-enroll on next login (once MFA is rolled out).`))
                        callAction(`/ops/users/${userId}/reset-mfa`)
                    }}
                    disabled={busy}
                    style={secondaryBtn}
                  >
                    <Smartphone size={14} aria-hidden="true" /> Reset MFA
                  </button>
                )}

                {/* Force sign-out — kills all active sessions */}
                <button
                  onClick={() => {
                    if (confirm(`Sign ${detail.account.email} out of all devices? Their current cookies stop working immediately.`))
                      callAction(`/ops/users/${userId}/force-sign-out`)
                  }}
                  disabled={busy}
                  style={secondaryBtn}
                >
                  <LogOut size={14} aria-hidden="true" /> Force sign-out
                </button>

                {/* Lock / unlock (temp hold) */}
                {detail.account.status === 'active' && (
                  <button
                    onClick={() => {
                      if (confirm(`Temporarily lock ${detail.account.email}? Their sessions are killed and they can't sign in until unlocked. Use deactivate for permanent disable.`))
                        callAction(`/ops/users/${userId}/lock`)
                    }}
                    disabled={busy}
                    style={secondaryBtn}
                  >
                    <Lock size={14} aria-hidden="true" /> Lock
                  </button>
                )}
                {detail.account.status === 'locked' && (
                  <button
                    onClick={() => callAction(`/ops/users/${userId}/unlock`)}
                    disabled={busy}
                    style={primaryBtn}
                  >
                    <Unlock size={14} aria-hidden="true" /> Unlock
                  </button>
                )}

                {/* Deactivate / Reactivate (existing) */}
                {detail.account.status === 'active' || detail.account.status === 'locked' ? (
                  <button
                    onClick={() => {
                      if (confirm(`Deactivate ${detail.account.email}? They will be signed out and can't sign in until reactivated. Deactivate is reversible; for permanent removal use Hard delete in the Danger zone below.`))
                        callAction(`/users/${userId}/deactivate`)
                    }}
                    disabled={busy}
                    style={dangerBtn}
                  >
                    <Power size={14} aria-hidden="true" /> Deactivate
                  </button>
                ) : (
                  <button
                    onClick={() => callAction(`/users/${userId}/reactivate`)}
                    disabled={busy}
                    style={primaryBtn}
                  >
                    <UserCheck size={14} aria-hidden="true" /> Reactivate
                  </button>
                )}
              </div>

              {/* Reveal cards for newly-generated reset links */}
              {revealedReset && (
                <RevealCard
                  title={`Password reset link for ${revealedReset.email}`}
                  link={revealedReset.link}
                  onDismiss={() => setRevealedReset(null)}
                  note="Single-use, expires in 60 minutes. Copy and send to the user."
                />
              )}
            </section>

            {/* Pharmacy assignment — SaaS/platform only; hidden for bound pharmacy admins */}
            {canReassignPharmacy && (
              <PharmacyAssignmentSection
                accountId={userId}
                currentPharmacyId={detail.account.pharmacy_id}
                busy={busy}
                onChanged={async () => { await reload(); onChanged() }}
                setError={setError}
                setBusy={setBusy}
              />
            )}

            {/* Roles */}
            <section>
              <h4 style={drawerSectionTitle}>Roles ({detail.roles.length})</h4>
              {detail.roles.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--color-text-mute)', margin: '4px 0' }}>
                  No roles assigned.
                </p>
              ) : (
                <ul style={chipList}>
                  {detail.roles.map(r => (
                    <li key={r.id} style={roleChip}>
                      <ShieldCheck size={12} aria-hidden="true" />
                      <span>{r.name}</span>
                      {r.is_system && <span style={miniBadge}>system</span>}
                      <button
                        onClick={() => {
                          if (confirm(`Revoke "${r.name}" from this user?`))
                            callAction(`/users/${userId}/revoke-role`, { role_id: r.id })
                        }}
                        disabled={busy}
                        aria-label={`Revoke ${r.name}`}
                        style={chipRemoveBtn}
                      >
                        <X size={12} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {availableRoles.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <label style={drawerSubLabel}>Assign role</label>
                  <select
                    onChange={e => {
                      const v = e.target.value
                      if (!v) return
                      callAction(`/users/${userId}/assign-role`, { role_id: v })
                      e.currentTarget.value = ''
                    }}
                    disabled={busy}
                    style={filterSelect}
                    defaultValue=""
                  >
                    <option value="">Pick a role to add…</option>
                    {availableRoles.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </section>

            {/* Prescriber access — nurse/provider roles only */}
            {/* D-RBAC-1 — groups this account is in (direct + via role / NPI / facility). */}
            <UserGroupsSection userId={userId} roles={detail.roles} canWrite={canWriteUsers} onChanged={onChanged} />
            <PrescriberAccessSection userId={userId} roles={detail.roles} canWrite={canWriteUsers} onChanged={onChanged}
              selfPrescriberId={(detail.account as any).prescriber_id}
              selfPrescriberName={(detail.account as any).prescriber_name} />
            {/* D-MFR-1 — same slot, same props. The Manufacturer role is assignable from
                THIS surface too (routed at /manage-users), so the grant has to be settable
                here or the role goes on with no way to scope it. */}
            <ManufacturerAccessSection userId={userId} roles={detail.roles} canWrite={canWriteUsers} onChanged={onChanged} />
            {/* D-SOC-3 — all / facility / residential scope for external accounts, then the facility grant. */}
            <SiteOfCareScopeSection userId={userId} roles={detail.roles} canWrite={canWriteUsers} onChanged={onChanged} />
            <FacilityAccessSection userId={userId} roles={detail.roles} canWrite={canWriteUsers} onChanged={onChanged} />
            <ProviderDrugFilterSection userId={userId} roles={detail.roles} canWrite={canWriteUsers} onChanged={onChanged} />

            {/* View access — which tabs this user can see */}
            <ViewAccessSection userId={userId} />

            {/* Effective permissions */}
            <section>
              <h4 style={drawerSectionTitle}>Effective permissions ({detail.permissions.length})</h4>
              {detail.permissions.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--color-text-mute)', margin: '4px 0' }}>
                  No effective permissions.
                </p>
              ) : (
                <ul style={permsList}>
                  {detail.permissions.map(p => <li key={p}><code style={codeStyle}>{p}</code></li>)}
                </ul>
              )}
            </section>

            {/* Activity — sessions + audit */}
            <ActivitySection accountId={userId} />

            {/* Danger zone — hard delete */}
            <DangerZoneSection
              accountId={userId}
              email={detail.account.email}
              isSynthetic={detail.account.is_synthetic}
              busy={busy}
              onDeleted={() => { onChanged(); onClose() }}
              setError={setError}
              setBusy={setBusy}
            />
          </div>
        )}
      </aside>
    </>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function PrescriberAccessSection({ userId, roles, canWrite, onChanged, selfPrescriberId, selfPrescriberName }: {
  userId: string
  roles: { id: string; name: string }[]
  canWrite: boolean
  onChanged: () => void
  /**
   * ClickUp 86cb2k1jz — the account's OWN prescriber (dbo.accounts.prescriber_id), written at
   * invite acceptance. Access is the UNION of this self-link and the account_prescriber_access
   * team grants (see RxFunctions.ResolveProviderScopePrescriberIdsAsync), but this panel only ever
   * listed the grants — so an invited Provider who could plainly see their own data was reported
   * here as "PRESCRIBER ACCESS (0) — sees no prescriber data". The data was right; the screen was
   * lying. GET /users/{id} already returns both fields, so this is a display fix only.
   */
  selfPrescriberId?: string | null
  selfPrescriberName?: string | null
}) {
  // Only relevant for nurse / provider roles (accounts that support a prescriber).
  // Mario, 2026-09-24: "select like specific provider for the facility user". The SERVER
  // already honours this — ProviderPrescriberScope.ResolveEffectiveAsync returns an explicit
  // grant when one exists (ids.Count > 0 short-circuits) and only falls back to "every
  // prescriber in the pharmacy" when the list is EMPTY. So a facility account narrowed to
  // one prescriber already works; the panel was simply never shown for it, because the gate
  // matched on role NAME and the role is called "Facility Staff".
  //
  // Leaving it empty keeps today's behaviour (facility grant is the only axis); picking
  // prescribers narrows on BOTH axes, which is what a facility serving several practices
  // needs.
  const show = roles.some(r => /nurse|provider|prescriber|facility/i.test(r.name))
  const [access, setAccess] = useState<{ prescriber_id: string; display_name: string | null; npi: string | null; specialty: string | null }[]>([])
  // Provider Admin (migration 379): the account sees every prescriber in its pharmacy through a
  // standing role, not through rows in this list. Without this the panel would report "(0) — sees
  // no prescriber data" for someone who sees all of it.
  const [allScope, setAllScope] = useState(false)
  const [adding, setAdding] = useState(false)
  const [q, setQ] = useState('')
  // Patrick, 2026-08-27: assigning a provider meant picking prescribers one at a time. The picker
  // now loads the pharmacy's whole roster so it can be checkbox-selected and granted in one call.
  const [roster, setRoster] = useState<api.PrescriberListItem[]>([])
  const [rosterTotal, setRosterTotal] = useState(0)
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [rosterLoading, setRosterLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)

  const loadAccess = useCallback(async () => {
    try {
      const d = await api.apiFetch(`/users/${userId}/prescriber-access`, { credentials: 'include' }).then(r => r.json())
      setAccess(d.items ?? [])
      setAllScope(!!d.all_scope)
    } catch { setAccess([]); setAllScope(false) }
  }, [userId])

  useEffect(() => { if (show) loadAccess() }, [show, loadAccess])

  // Whole roster on open. Search still narrows it, but with no search term the list is now the
  // full set rather than nothing — you cannot "select all" of a list that only appears once you
  // have typed two characters.
  //
  // PAGED, because asking for the lot in one request does not work: PageRequest clamps page_size to
  // MaxPageSize = 200 SILENTLY, so an earlier version that asked for 500 got 200 and "Select all"
  // quietly meant "select the first 200" (Patrick, 2026-08-28). Raising that ceiling would change
  // every paged endpoint in the product, so the picker pages instead.
  useEffect(() => {
    if (!adding) { setRoster([]); setPicked(new Set()); setRosterTotal(0); return }
    let cancelled = false
    ;(async () => {
      const PAGE = 200        // the server's own maximum; asking for more is clamped, not honoured
      const MAX_PAGES = 15    // 3,000 prescribers — a bound so a huge roster cannot hang the drawer
      setRosterLoading(true)
      try {
        const first = await api.listPrescribersPaged({ page: 1, pageSize: PAGE })
        const total = first.total ?? first.items.length
        const pages = Math.min(first.total_pages ?? 1, MAX_PAGES)
        const all = [...first.items]
        for (let p = 2; p <= pages; p++) {
          if (cancelled) return
          const next = await api.listPrescribersPaged({ page: p, pageSize: PAGE })
          all.push(...next.items)
        }
        if (!cancelled) { setRoster(all); setRosterTotal(total) }
      } catch { if (!cancelled) { setRoster([]); setRosterTotal(0) } }
      finally { if (!cancelled) setRosterLoading(false) }
    })()
    return () => { cancelled = true }
  }, [adding])

  if (!show) return null

  /** Grant every checked prescriber in one call. */
  async function grantMany(ids: string[]) {
    if (ids.length === 0) return
    setBusy(true); setErr(null); setNote(null)
    try {
      const r = await api.apiFetch(`/users/${userId}/prescriber-access`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prescriber_ids: ids }),
      })
      if (!r.ok) throw await api.parseApiError(r)
      const d = await r.json()
      // Report what actually happened rather than "done": re-running a select-all grants nothing
      // new, and saying "granted 12" when 12 were already there would be a lie the screen tells.
      const parts: string[] = []
      if (d.newly_granted > 0) parts.push(`Granted ${d.newly_granted}`)
      if (d.already_had > 0) parts.push(`${d.already_had} already had access`)
      if (d.rejected_not_in_team?.length) parts.push(`${d.rejected_not_in_team.length} not on this pharmacy's team`)
      setNote(parts.join(' · ') || 'No change.')
      setPicked(new Set()); setAdding(false); setQ(''); await loadAccess(); onChanged()
    } catch (e: any) {
      setErr(e?.body?.error === 'too_many_prescribers' ? (e?.body?.message || 'Too many prescribers in one grant.')
           : e?.body?.error === 'prescriber_not_in_team' ? 'None of those prescribers are on this pharmacy’s team.'
           : (e?.body?.message || e?.message || 'Could not grant access.'))
    } finally { setBusy(false) }
  }

  async function remove(prescriberId: string) {
    setBusy(true); setErr(null); setNote(null)
    try {
      const r = await api.apiFetch(`/users/${userId}/prescriber-access/${prescriberId}`, { method: 'DELETE', credentials: 'include' })
      if (!r.ok) throw await api.parseApiError(r)
      setNote('Access removed.'); await loadAccess(); onChanged()
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'Could not remove access.') }
    finally { setBusy(false) }
  }

  const chipBtn: React.CSSProperties = { padding: '3px 10px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-surface)', fontSize: 12, cursor: 'pointer' }

  return (
    <section>
      {/* 86cb2k1jz: the count is the UNION — self-link + team grants — because that is what the
          backend actually scopes on. Counting only the grants is what produced "(0)" for a
          provider who could see their own patients. */}
      <h4 style={drawerSectionTitle}>
        Prescriber access ({allScope ? 'all' : access.length + (selfPrescriberId ? 1 : 0)})
      </h4>
      <p style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '0 0 8px' }}>
        Which prescribers’ prescriptions &amp; patients this user can see. Only this pharmacy’s prescribers can be added.
      </p>
      {allScope && (
        <div style={{ padding: '8px 11px', marginBottom: 8, borderRadius: 8,
                      border: '1px solid var(--color-primary, #0d9488)', background: 'var(--color-surface-alt)', fontSize: 12.5 }}>
          <strong>Provider Admin — sees every prescriber in this pharmacy.</strong>{' '}
          <span style={{ color: 'var(--color-text-mute)' }}>
            A standing rule from the role, so prescribers added later are included automatically.
            Individual grants below are redundant while this role is assigned; remove the role to
            fall back to them.
          </span>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 6 }}>{err}</div>}
      {note && <div style={{ fontSize: 12, color: 'var(--color-primary, #0d9488)', marginBottom: 6 }}>{note}</div>}
      {selfPrescriberId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 8, marginBottom: 6, background: 'var(--color-surface-alt)' }}>
          <span style={{ fontSize: 13 }}>
            <strong>{selfPrescriberName || 'Own prescriber record'}</strong>{' '}
            <span style={{ color: 'var(--color-text-mute)' }}>· own record (linked at invitation)</span>
          </span>
        </div>
      )}
      {/* No Remove button on the self-link on purpose: it is set by the invitation, and clearing it
          changes what the provider can see of THEIR OWN data. That is a different decision from
          revoking a team grant, so it does not belong behind the same one-click button. */}
      {access.length === 0 ? (
        selfPrescriberId ? null : (
        <p style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '0 0 8px' }}><em>None granted — this user sees no prescriber data until you add one.</em></p>
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
          {access.map(a => (
            <div key={a.prescriber_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 8 }}>
              <span style={{ fontSize: 13 }}><strong>{a.display_name || '—'}</strong> <span style={{ color: 'var(--color-text-mute)' }}>· NPI {a.npi || '—'}{a.specialty ? ` · ${a.specialty}` : ''}</span></span>
              {canWrite && <button style={{ ...chipBtn, marginLeft: 'auto' }} disabled={busy} onClick={() => remove(a.prescriber_id)}>Remove</button>}
            </div>
          ))}
        </div>
      )}
      {canWrite && (!adding ? (
        <button style={chipBtn} disabled={busy} onClick={() => { setAdding(true); setQ('') }}>+ Add prescriber</button>
      ) : (
        (() => {
          // Everyone this user does not already have, narrowed by the search box.
          const already = new Set(access.map(a => a.prescriber_id))
          const term = q.trim().toLowerCase()
          const candidates = roster
            .filter(p => !already.has(p.id))
            .filter(p => !term
              || p.display_name?.toLowerCase().includes(term)
              || (p.npi ?? '').toLowerCase().includes(term)
              || (p.specialty ?? '').toLowerCase().includes(term))
          const allChecked = candidates.length > 0 && candidates.every(p => picked.has(p.id))
          const toggle = (id: string) => {
            const next = new Set(picked)
            next.has(id) ? next.delete(id) : next.add(id)
            setPicked(next)
          }
          return (
            <div>
              <input autoFocus value={q} onChange={e => setQ(e.target.value)}
                placeholder="Filter this pharmacy’s prescribers…" style={filterSelect} />

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 6px', fontSize: 12.5 }}>
                {/* Disabled while the roster is still paging in — otherwise "Select all" would tick
                    whatever had arrived so far and read as if it had selected everyone. */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6,
                                cursor: rosterLoading ? 'wait' : 'pointer', fontWeight: 600 }}>
                  <input type="checkbox" checked={allChecked}
                    disabled={busy || rosterLoading || candidates.length === 0}
                    onChange={() => setPicked(allChecked ? new Set() : new Set(candidates.map(p => p.id)))} />
                  {rosterLoading ? 'Loading prescribers…'
                    : term ? `Select all ${candidates.length} matching`
                    : `Select all ${candidates.length}`}
                </label>
                <span style={{ color: 'var(--color-text-mute)' }}>
                  {picked.size} selected{already.size > 0 ? ` · ${already.size} already granted` : ''}
                </span>
              </div>

              {/* Snapshot, not a standing rule — said here rather than in a tooltip, because the
                  difference only bites later, when a new prescriber is onboarded and this user
                  silently does not see them. */}
              <p style={{ fontSize: 11.5, color: 'var(--color-text-mute)', margin: '0 0 8px' }}>
                Grants the prescribers selected now. Prescribers added to this pharmacy later are
                <strong> not</strong> included automatically.
              </p>

              {/* The roster request is capped. If a pharmacy has more prescribers than we loaded,
                  "Select all" would silently mean "all of the ones we happened to fetch" — so say so. */}
              {!rosterLoading && rosterTotal > roster.length && (
                <p style={{ fontSize: 11.5, color: '#b45309', margin: '0 0 8px' }}>
                  <strong>Showing {roster.length} of {rosterTotal} prescribers.</strong> This roster is
                  larger than the picker loads, so “Select all” covers only the {roster.length} listed.
                  Filter to reach the rest, or use the Provider Admin role for standing access to all.
                </p>
              )}

              {candidates.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                  <em>{roster.length === 0 ? 'No prescribers found for this pharmacy.' : 'This user already has access to every prescriber shown.'}</em>
                </p>
              ) : (
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 8 }}>
                  {candidates.map(p => (
                    <label key={p.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                               borderBottom: '1px solid var(--color-border)', fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={picked.has(p.id)} disabled={busy} onChange={() => toggle(p.id)} />
                      <span>
                        <strong>{p.display_name}</strong>{' '}
                        <span style={{ color: 'var(--color-text-mute)' }}>· NPI {p.npi}{p.specialty ? ` · ${p.specialty}` : ''}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                <button style={{ ...chipBtn, fontWeight: 600 }} disabled={busy || picked.size === 0}
                  onClick={() => grantMany([...picked])}>
                  {busy ? 'Granting…' : `Grant access${picked.size ? ` (${picked.size})` : ''}`}
                </button>
                <button style={chipBtn} disabled={busy} onClick={() => { setAdding(false); setQ(''); setPicked(new Set()) }}>Cancel</button>
              </div>
            </div>
          )
        })()
      ))}
    </section>
  )
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt style={kvLabel}>{label}</dt>
      <dd style={kvValue}>{children}</dd>
    </>
  )
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'active'   ? { bg: '#dcfce7', fg: '#166534' } :
    status === 'locked'   ? { bg: '#fef3c7', fg: '#854d0e' } :
    status === 'disabled' ? { bg: '#fee2e2', fg: '#991b1b' } :
    status === 'pending'  ? { bg: '#dbeafe', fg: '#1e40af' } :
                            { bg: '#f3f4f6', fg: '#4b5563' }
  return <span style={{ ...statusPillStyle, background: tone.bg, color: tone.fg }}>{status}</span>
}

// ============================================================================
// Helpers
// ============================================================================

// Friendly role labels for the Users screen (renames are display-only here until the system-wide
// role rename lands): system "Admin" = SaaS admin, "Owner" = Pharmacy admin.
const ROLE_LABELS: Record<string, string> = { Admin: 'SaaS admin', Owner: 'Pharmacy admin' }
const roleLabel = (csv: string): string =>
  (csv || '').split(',').map(s => s.trim()).filter(Boolean).map(n => ROLE_LABELS[n] || n).join(', ')

const saasBadge: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: '#6d28d9', background: '#f5f3ff',
  border: '1px solid #ddd6fe', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap',
}

interface UserListItem {
  id: string
  email: string
  display_name: string
  status: string
  email_verified: boolean
  is_synthetic: boolean
  last_login_at: string | null
  created_at: string
  pharmacy_id: string | null
  pharmacy_name: string | null
  roles: string
}

// D-UI-2: what each column of the users list shows, for its header funnel.
const USER_FUNNELS: FunnelColumn<UserListItem>[] = [
  { key: 'email',  label: 'Email',        value: u => u.email },
  { key: 'name',   label: 'Display name', value: u => u.display_name },
  { key: 'status', label: 'Status',       value: u => u.status },
  { key: 'access', label: 'Access',       value: u => u.pharmacy_id ? (u.pharmacy_name || 'Pharmacy') : 'SaaS · Owl Consultations' },
  { key: 'roles',  label: 'Roles',        value: u => roleLabel(u.roles) || 'none' },
  { key: 'login',  label: 'Last login',   value: u => u.last_login_at },
]

interface RoleSummary {
  id: string
  name: string
  description: string | null
  is_system: boolean
  user_count: number
  permission_count: number
}

async function throwIfBad(r: Response): Promise<any> {
  const data = await r.json().catch(() => null)
  if (!r.ok) throw new api.ApiError(r.status, data, data?.error ?? `HTTP ${r.status}`)
  return data
}

function relTime(iso: string): string {
  const t = new Date(iso).getTime()
  const ms = Date.now() - t
  const m = Math.floor(ms / 60000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d}d ago`
  return formatUsDateLocal(iso)
}

// ============================================================================
// WI-898: SaaS user management — sections used inside UserDrawer
// ============================================================================

function PharmacyAssignmentSection({
  accountId, currentPharmacyId, busy, onChanged, setError, setBusy,
}: {
  accountId: string
  currentPharmacyId: string | null
  busy: boolean
  onChanged: () => Promise<void>
  setError: (s: string | null) => void
  setBusy: (b: boolean) => void
}) {
  const [pharmacies, setPharmacies] = useState<api.PharmacyListItem[]>([])
  const [selected, setSelected] = useState<string>(currentPharmacyId ?? '')
  useEffect(() => { setSelected(currentPharmacyId ?? '') }, [currentPharmacyId])

  useEffect(() => {
    api.listPharmacies().then(r => setPharmacies(r.items)).catch(() => setPharmacies([]))
  }, [])

  const currentName = currentPharmacyId
    ? (pharmacies.find(p => p.id === currentPharmacyId)?.name ?? '(unknown)')
    : 'SaaS-unbound (no pharmacy)'

  async function save() {
    if (selected === (currentPharmacyId ?? '')) return
    const target = selected === '' ? null : selected
    const targetName = target
      ? (pharmacies.find(p => p.id === target)?.name ?? '(unknown)')
      : 'SaaS-unbound (no pharmacy)'
    if (!confirm(`Move this user from "${currentName}" to "${targetName}"?\n\nTheir prior pharmacy-scoped data becomes invisible to them immediately. Active sessions are NOT killed — use Force sign-out if you want to invalidate them.`)) return
    setBusy(true); setError(null)
    try {
      await api.assignUserPharmacy(accountId, target)
      await onChanged()
    } catch (e) {
      setError(friendlyError(e))
      setSelected(currentPharmacyId ?? '')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      <h4 style={drawerSectionTitle}>
        <Building2 size={14} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 6 }} />
        Pharmacy assignment
      </h4>
      <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8 }}>
        Currently: <strong>{currentName}</strong>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={selected}
          onChange={e => setSelected(e.target.value)}
          disabled={busy}
          style={{ ...filterSelect, flex: 1 }}
        >
          <option value="">— SaaS-unbound (no pharmacy) —</option>
          {pharmacies.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <button
          onClick={save}
          disabled={busy || selected === (currentPharmacyId ?? '')}
          style={primaryBtn}
        >
          {busy ? <Loader2 size={13} className="spin" /> : null}
          Save assignment
        </button>
      </div>
    </section>
  )
}

function ActivitySection({ accountId }: { accountId: string }) {
  const [sessions, setSessions] = useState<api.UserSession[]>([])
  const [activity, setActivity] = useState<api.UserActivityEvent[]>([])
  const [tab, setTab] = useState<'sessions' | 'audit'>('sessions')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    Promise.allSettled([
      api.listUserSessions(accountId),
      api.listUserActivity(accountId),
    ]).then(([s, a]) => {
      if (cancelled) return
      if (s.status === 'fulfilled') setSessions(s.value.items)
      if (a.status === 'fulfilled') setActivity(a.value.items)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [accountId])

  return (
    <section>
      <h4 style={drawerSectionTitle}>
        <History size={14} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 6 }} />
        Activity
      </h4>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button
          onClick={() => setTab('sessions')}
          style={tab === 'sessions' ? tabBtnActive : tabBtn}
        >
          <Monitor size={11} aria-hidden="true" /> Sessions ({sessions.length})
        </button>
        <button
          onClick={() => setTab('audit')}
          style={tab === 'audit' ? tabBtnActive : tabBtn}
        >
          Audit ({activity.length})
        </button>
      </div>

      {loading && <Loader2 size={14} className="spin" />}

      {!loading && tab === 'sessions' && (
        sessions.length === 0 ? (
          <p style={emptyText}>No sessions recorded.</p>
        ) : (
          <ul style={activityList}>
            {sessions.slice(0, 10).map(s => {
              const active = !s.revoked_at && new Date(s.expires_at) > new Date()
              return (
                <li key={s.id} style={activityRow}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    background: active ? '#10b981' : '#9ca3af',
                    flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12 }}>
                      {active ? 'Active' : 'Revoked'}
                      {s.ip && <span style={{ color: 'var(--color-text-soft)' }}> · {s.ip}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>
                      Created {relTime(s.created_at)}
                      {s.last_seen_at && ` · last seen ${relTime(s.last_seen_at)}`}
                      {s.revoke_reason && ` · ${s.revoke_reason}`}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )
      )}

      {!loading && tab === 'audit' && (
        activity.length === 0 ? (
          <p style={emptyText}>No audit events on this account yet.</p>
        ) : (
          <ul style={activityList}>
            {activity.slice(0, 15).map(a => (
              <li key={a.id} style={activityRow}>
                <code style={auditActionCode}>{a.action}</code>
                <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: 'var(--color-text-mute)' }}>
                  {relTime(a.occurred_at)}
                  {a.actor_account && ` · by ${a.actor_account}`}
                </div>
              </li>
            ))}
          </ul>
        )
      )}
    </section>
  )
}

function DangerZoneSection({
  accountId, email, isSynthetic, busy, onDeleted, setError, setBusy,
}: {
  accountId: string
  email: string
  isSynthetic: boolean
  busy: boolean
  onDeleted: () => void
  setError: (s: string | null) => void
  setBusy: (b: boolean) => void
}) {
  const [confirmEmail, setConfirmEmail] = useState('')

  async function hardDelete() {
    if (confirmEmail.toLowerCase().trim() !== email.toLowerCase()) {
      setError('Email confirmation does not match.')
      return
    }
    if (!confirm(`HARD DELETE ${email}? This removes the account row and all sessions/role grants. Audit trail is preserved by display-name string. This action cannot be undone.`)) return
    setBusy(true); setError(null)
    try {
      await api.hardDeleteUser(accountId, email)
      onDeleted()
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section style={{ marginTop: 8 }}>
      <h4 style={{ ...drawerSectionTitle, color: '#991b1b' }}>
        <Trash2 size={14} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 6 }} />
        Danger zone
      </h4>
      {isSynthetic ? (
        <p style={{ fontSize: 12, color: 'var(--color-text-soft)' }}>
          This is a demo (synthetic) account, seeded by migrations. It can't be
          hard-deleted — it would be re-created on the next deploy. Deactivate
          instead if you need to disable it.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8 }}>
            Hard delete removes the account row, all sessions, all role grants.
            Audit trail is kept (by display-name string). <strong>Cannot be undone.</strong>
            Prefer Deactivate unless you need to free up the email address for re-use.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              placeholder={`Type "${email}" to confirm`}
              value={confirmEmail}
              onChange={e => setConfirmEmail(e.target.value)}
              disabled={busy}
              style={{ ...filterSelect, flex: 1 }}
            />
            <button
              onClick={hardDelete}
              disabled={busy || confirmEmail.toLowerCase().trim() !== email.toLowerCase()}
              style={dangerBtn}
            >
              <Trash2 size={13} aria-hidden="true" /> Hard delete
            </button>
          </div>
        </>
      )}
    </section>
  )
}

function RevealCard({ title, link, note, onDismiss }: {
  title: string; link: string; note: string; onDismiss: () => void
}) {
  const fullUrl = `${window.location.origin}${link}`
  return (
    <div style={revealCardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{title}</div>
          <code style={revealLink}>{fullUrl}</code>
          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--color-text-soft)' }}>{note}</div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => { navigator.clipboard.writeText(fullUrl); }} style={tabBtn}>
            <Copy size={12} /> Copy
          </button>
          <button onClick={onDismiss} style={iconBtn} aria-label="Dismiss"><X size={14} /></button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Styles
// ============================================================================

const toolbar: React.CSSProperties = {
  display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap',
}
const searchBox: React.CSSProperties = { position: 'relative', minWidth: 280 }
const searchIcon: React.CSSProperties = {
  position: 'absolute', left: 10, top: 12, color: 'var(--color-text-mute)',
}
const searchInput: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px 8px 32px',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--color-surface)',
  fontFamily: 'inherit',
}
const filterSelect: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--color-surface)',
  fontFamily: 'inherit',
}

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  overflow: 'hidden',
}
const tableHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 14px',
  background: 'var(--color-surface-alt)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
  color: 'var(--color-text-mute)', textTransform: 'uppercase',
}
const tableRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '12px 14px', width: '100%',
  background: 'transparent',
  border: 'none', borderBottom: '1px solid var(--color-border)',
  fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer',
}
const tableRowActive: React.CSSProperties = {
  background: 'var(--color-primary-soft, #ccfbf1)',
}
const truncate: React.CSSProperties = {
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
}

const statusPillStyle: React.CSSProperties = {
  display: 'inline-block', fontSize: 11, fontWeight: 600,
  padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: 0.5,
}
const syntheticPill: React.CSSProperties = {
  marginLeft: 8, fontSize: 10, fontWeight: 600,
  background: '#fef3c7', color: '#854d0e',
  padding: '1px 6px', borderRadius: 8, letterSpacing: 0.5,
}

const loadingWrap: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60,
}
const errorWrap: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '12px 16px',
  background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 6,
  color: '#991b1b', fontSize: 13,
}
const emptyWrap: React.CSSProperties = {
  textAlign: 'center', padding: 60, color: 'var(--color-text-soft)',
  background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8,
}

// Drawer
const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.4)', zIndex: 50,
}
const drawerStyle: React.CSSProperties = {
  position: 'fixed', top: 0, right: 0, bottom: 0,
  width: 'min(520px, 100vw)',
  background: 'var(--color-surface, #fff)',
  borderLeft: '1px solid var(--color-border)',
  boxShadow: 'var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.08))',
  zIndex: 51,
  display: 'flex', flexDirection: 'column',
}
const drawerHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 16px',
  borderBottom: '1px solid var(--color-border)',
}
const drawerBody: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: 16,
  display: 'flex', flexDirection: 'column', gap: 24,
}
const drawerSectionTitle: React.CSSProperties = {
  margin: '0 0 8px', fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
  color: 'var(--color-text-mute)', textTransform: 'uppercase',
}
const drawerSubLabel: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
  color: 'var(--color-text-mute)', marginBottom: 6, textTransform: 'uppercase',
}
const kvList: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px',
  margin: 0, fontSize: 13,
}
const kvLabel: React.CSSProperties = { color: 'var(--color-text-mute)', fontSize: 12 }
const kvValue: React.CSSProperties = { margin: 0, color: 'var(--color-text)', overflowWrap: 'anywhere', minWidth: 0 }
const miniBadge: React.CSSProperties = {
  marginLeft: 6, fontSize: 10, fontWeight: 600,
  background: '#dcfce7', color: '#166534',
  padding: '1px 6px', borderRadius: 8, letterSpacing: 0.5,
}
const chipList: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 6, margin: 0, padding: 0, listStyle: 'none',
}
const roleChip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 8px',
  background: 'var(--color-primary-soft)',
  color: 'var(--color-primary-hover)',
  borderRadius: 12,
  fontSize: 12, fontWeight: 500,
}
const chipRemoveBtn: React.CSSProperties = {
  background: 'transparent', border: 'none',
  color: 'var(--color-primary-hover)',
  cursor: 'pointer', padding: 0, display: 'inline-flex',
}
const permsList: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 4,
  margin: 0, padding: 0, listStyle: 'none',
}
const codeStyle: React.CSSProperties = {
  background: 'var(--color-surface-alt)',
  padding: '2px 6px', borderRadius: 4,
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11.5, color: 'var(--color-text-soft)',
}
const iconBtn: React.CSSProperties = {
  background: 'transparent', border: 'none',
  padding: 6, borderRadius: 6, cursor: 'pointer',
  color: 'var(--color-text-mute)',
}
const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px',
  background: 'var(--color-primary)', color: '#fff',
  border: 'none', borderRadius: 6,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
}
const dangerBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px',
  background: '#fee2e2', color: '#991b1b',
  border: '1px solid #fecaca', borderRadius: 6,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
}

// WI-898 additions
const secondaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px',
  background: '#fff', color: 'var(--color-text)',
  border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 13, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
}
const tabBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px',
  background: 'transparent', color: 'var(--color-text-soft)',
  border: '1px solid var(--color-border)', borderRadius: 4,
  fontSize: 11, fontWeight: 500, cursor: 'pointer',
  fontFamily: 'inherit',
}
const tabBtnActive: React.CSSProperties = {
  ...tabBtn,
  background: 'var(--color-primary)', color: '#fff',
  borderColor: 'var(--color-primary)',
}
const emptyText: React.CSSProperties = {
  fontSize: 12, color: 'var(--color-text-mute)', margin: '4px 0',
}
const activityList: React.CSSProperties = {
  listStyle: 'none', margin: 0, padding: 0,
  display: 'flex', flexDirection: 'column', gap: 4,
}
const activityRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 8px',
  background: 'var(--color-surface-alt, rgba(0,0,0,0.02))',
  borderRadius: 4,
}
const auditActionCode: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 10.5,
  background: 'rgba(0,0,0,0.04)', padding: '1px 6px', borderRadius: 3,
  color: 'var(--color-text)', flexShrink: 0,
}
const revealCardStyle: React.CSSProperties = {
  marginTop: 12,
  background: '#ecfdf5', border: '1px solid #6ee7b7',
  borderRadius: 6, padding: 12,
}
const revealLink: React.CSSProperties = {
  display: 'block', fontFamily: 'monospace', fontSize: 11,
  background: '#fff', padding: '6px 8px', borderRadius: 3,
  border: '1px solid #d1fae5', color: '#065f46',
  wordBreak: 'break-all', maxWidth: '100%',
}

// ── View Access Section (in UserDrawer) ────────────────────────────
function ViewAccessSection({ userId }: { userId: string }) {
  const { isDisabled } = useModules()
  const [data, setData] = useState<{ assigned_tabs: string[]; custom_views: { id: string; name: string }[] } | null>(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const SYSTEM_TABS = [
    { key: 'intake', label: 'Intake' },
    { key: 'insurance', label: 'Awaiting insurance' },
    { key: 'production', label: 'Production' },
    { key: 'ready', label: 'Ready & out' },
    { key: 'hold', label: 'On hold' },
    { key: 'refills', label: 'Refills' },
    { key: 'all', label: 'All prescriptions' },
  ]

  useEffect(() => {
    setLoading(true)
    api.getUserTabAccess(userId)
      .then(d => {
        setData(d)
        setSelected(new Set(d.assigned_tabs))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [userId])

  const toggle = (key: string) => {
    const next = new Set(selected)
    next.has(key) ? next.delete(key) : next.add(key)
    setSelected(next)
    setSaved(false)
  }

  const [saveError, setSaveError] = useState<string | null>(null)

  const save = async () => {
    setSaving(true); setSaveError(null)
    try {
      await api.updateUserTabAccess(userId, Array.from(selected))
      setSaved(true)
    } catch (e: any) {
      setSaveError(typeof e === 'object' && e.message ? e.message : 'Failed to save. Check permissions.')
    }
    finally { setSaving(false) }
  }

  if (isDisabled('queues'))
    return (
      <section>
        <h4 style={drawerSectionTitle}>View access</h4>
        <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: 0 }}>
          Custom views and per-user tab access require the Queues module, which is not enabled for this pharmacy.
        </p>
      </section>
    )

  if (loading) return <section><h4 style={drawerSectionTitle}>View access</h4><p style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>Loading…</p></section>

  const allTabs = [
    ...SYSTEM_TABS,
    ...(data?.custom_views || []).map(v => ({ key: `cv_${v.id}`, label: v.name })),
  ]

  return (
    <section>
      <h4 style={drawerSectionTitle}>View access ({selected.size} of {allTabs.length} tabs)</h4>
      <p style={{ fontSize: 11, color: 'var(--color-text-mute)', margin: '0 0 6px' }}>
        Check the tabs this user can see on the Prescriptions page. Unchecked tabs are hidden for this user.
      </p>
      <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 4, padding: 6, marginBottom: 8 }}>
        <div style={{ marginBottom: 4 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-mute)', textTransform: 'uppercase' }}>System tabs</span>
        </div>
        {SYSTEM_TABS.map(t => (
          <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px', cursor: 'pointer', fontSize: 12 }}>
            <input type="checkbox" checked={selected.has(t.key)} onChange={() => toggle(t.key)} />
            {t.label}
          </label>
        ))}
        {data?.custom_views && data.custom_views.length > 0 && (<>
          <div style={{ marginTop: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-mute)', textTransform: 'uppercase' }}>Custom views</span>
          </div>
          {data.custom_views.map(v => (
            <label key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 4px', cursor: 'pointer', fontSize: 12 }}>
              <input type="checkbox" checked={selected.has(`cv_${v.id}`)} onChange={() => toggle(`cv_${v.id}`)} />
              {v.name}
            </label>
          ))}
        </>)}
      </div>
      {saveError && <p style={{ fontSize: 11, color: '#dc2626', margin: '4px 0' }}>{saveError}</p>}
      <button onClick={save} disabled={saving || saved} style={{
        ...secondaryBtn, fontSize: 12, marginTop: 4,
        background: saved ? '#d1fae5' : undefined,
        color: saved ? '#065f46' : undefined,
        fontWeight: 600,
      }}>
        {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save view access'}
      </button>
    </section>
  )
}
